import type { QueryKey } from '@tanstack/react-query';
import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
  type DynamicFilterOptionsPage,
  type FilterOptionsParams,
} from '@/components/DataTable';
import {
  INSTALLED_TOOLS_FILTERABLE_FIELDS,
  type InstalledToolListItem,
  type InstalledToolsFilterOptionField,
} from '@kirin/api-client/contracts/shadow-ai-installed-tools';
import { getShadowAiCategoryLabel } from '@kirin/api-client/contracts/shadow-ai';
import { INSTALLED_TOOLS_FILTER_URL_KEYS } from './constants';
import {
  NameCell,
  CategoryCell,
  FirstDetectedCell,
  LastSeenCell,
  EmptyOperators,
  EndpointCountCell,
  MatchedEndpointChip,
  endpointFilterConfig,
} from '@/components/shared/shadow-ai';
import { UsersCell, LoggedInUserCell } from './CellRenderers';
import {
  CategoryFilterOption,
  InstalledToolsUserFilterOption,
} from './FilterComponents';
import { fetchInstalledAiToolsFilterOptions } from './api';
import { queryKeys } from '@/hooks/api/queryKeys';
import { TIMESTAMP_COLUMN_MIN_WIDTH_LONG_HEADER } from '@/components/shared/timestampColumnWidth';

const StringOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
  ...EmptyOperators,
];

const EnumOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  ...EmptyOperators,
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
  ...EmptyOperators,
];

export function createInstalledAiToolsColumns(
  organizationId: string,
  showLoggedInUser = false,
  // KIR-3495: on the Shadow AI user-scope page the dropdowns must offer only
  // the values present in that member's rows, not the whole organization's.
  scopeUserId?: string,
  // KIR-5098: endpoint_identifier — "Endpoints" header + hostnames when on.
  showEndpointName = false
): ColumnConfig<InstalledToolListItem>[] {
  const optionsQueryKey = (
    field: InstalledToolsFilterOptionField,
    params: FilterOptionsParams
  ): QueryKey => {
    const base = queryKeys.shadowAi.installedAiToolsFilterOptions(
      organizationId,
      field,
      params
    );
    // Keep the org-wide key byte-identical when unscoped so the existing cache
    // entries stay valid.
    return scopeUserId ? [...base, { scopeUserId }] : [...base];
  };

  const fetchOptions = (
    field: InstalledToolsFilterOptionField,
    params: FilterOptionsParams
  ): Promise<DynamicFilterOptionsPage> =>
    fetchInstalledAiToolsFilterOptions(
      organizationId,
      field,
      params,
      scopeUserId
    );

  return [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      renderCell: ({ row }) => (
        // KIR-5098: the endpoint(s) matching an endpoint filter, on the closed row.
        <div className="flex min-w-0 flex-col items-start gap-1">
          <NameCell row={row} />
          <MatchedEndpointChip
            matchedEndpoints={row.matchedEndpoints}
            matchedEndpointCount={row.matchedEndpointCount}
          />
        </div>
      ),
      width: 200,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey:
          INSTALLED_TOOLS_FILTER_URL_KEYS[
            INSTALLED_TOOLS_FILTERABLE_FIELDS.NAME
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params => optionsQueryKey('name', params),
        queryFn: params => fetchOptions('name', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
      },
    },
    {
      id: 'category',
      header: 'Category',
      accessor: 'category',
      renderCell: ({ row }) => <CategoryCell row={row} />,
      width: 160,
      sortable: true,
      sortField: 'category',
      sortKey: 'cat',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey:
          INSTALLED_TOOLS_FILTER_URL_KEYS[
            INSTALLED_TOOLS_FILTERABLE_FIELDS.CATEGORY
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params => optionsQueryKey('category', params),
        queryFn: params => fetchOptions('category', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        labelFormatter: getShadowAiCategoryLabel,
        render: (option, isSelected) => (
          <CategoryFilterOption
            category={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    ...(showLoggedInUser
      ? [
          {
            id: 'loggedInUser',
            header: 'Logged-in User',
            accessor: 'loggedInUserCounts',
            renderCell: ({ row }) => <LoggedInUserCell row={row} />,
            width: 240,
            // KIR-3429: lexicographic sort on (personal, corporate, matched);
            // computed in the backend handler, not SQL.
            sortable: true,
            sortField: 'loggedInUser',
            sortKey: 'liu',
            filterable: true,
            // KIR-3429: mirror the Name column — a multiselect of the distinct
            // logged-in accounts (DYNAMIC options) plus contains/not-contains.
            dataType: ColumnDataTypes.STRING,
            filterOperators: StringOperators,
            statePersistence: {
              urlKey:
                INSTALLED_TOOLS_FILTER_URL_KEYS[
                  INSTALLED_TOOLS_FILTERABLE_FIELDS.LOGGED_IN_USER
                ],
            },
            filterOptions: {
              type: FilterOptionTypes.DYNAMIC,
              getQueryKey: params => optionsQueryKey('loggedInUser', params),
              queryFn: params => fetchOptions('loggedInUser', params),
              pagination: { mode: 'offset', limit: 10 },
              queryConfig: { staleTime: 2 * 60 * 1000 },
              // KIR-3657: list logged-in accounts like the Users column —
              // the account email, plus the member name when it resolves.
              render: (option, isSelected) => (
                <InstalledToolsUserFilterOption
                  userId={option.value}
                  userName={(option.metadata?.name as string | null) ?? null}
                  userEmail={(option.metadata?.email as string | null) ?? null}
                  isSelected={isSelected}
                />
              ),
            },
          } satisfies ColumnConfig<InstalledToolListItem>,
        ]
      : []),
    // KIR-3495: scoped to one member the count is always 1 — drop the column.
    ...(scopeUserId
      ? []
      : [
          {
            id: 'users',
            header: 'Users',
            accessor: 'userCount',
            renderCell: ({ row }) => <UsersCell row={row} />,
            width: 100,
            sortable: true,
            sortField: 'users',
            sortKey: 'uc',
            filterable: true,
            dataType: ColumnDataTypes.ENUM,
            filterOperators: EnumOperators,
            statePersistence: {
              urlKey:
                INSTALLED_TOOLS_FILTER_URL_KEYS[
                  INSTALLED_TOOLS_FILTERABLE_FIELDS.USERS
                ],
            },
            filterOptions: {
              type: FilterOptionTypes.DYNAMIC,
              getQueryKey: params => optionsQueryKey('users', params),
              queryFn: params => fetchOptions('users', params),
              pagination: { mode: 'offset', limit: 10 },
              queryConfig: { staleTime: 2 * 60 * 1000 },
              render: (option, isSelected) => (
                <InstalledToolsUserFilterOption
                  userId={option.value}
                  userName={(option.metadata?.name as string | null) ?? null}
                  userEmail={(option.metadata?.email as string | null) ?? null}
                  isSelected={isSelected}
                />
              ),
              tooltip: {
                render: option => option.label ?? option.value,
              },
            },
          } satisfies ColumnConfig<InstalledToolListItem>,
        ]),
    {
      // KIR-5098: distinct endpoints; its filter is the shared name-or-ID one.
      id: INSTALLED_TOOLS_FILTERABLE_FIELDS.ENDPOINT,
      header: showEndpointName ? 'Endpoints' : 'Machines',
      accessor: 'endpointCount',
      renderCell: ({ row }) => <EndpointCountCell count={row.endpointCount} />,
      width: 120,
      sortable: true,
      sortField: 'endpoint',
      sortKey: 'ep',
      ...endpointFilterConfig({
        getQueryKey: params => optionsQueryKey('endpoint', params),
        queryFn: params => fetchOptions('endpoint', params),
      }),
      statePersistence: {
        urlKey:
          INSTALLED_TOOLS_FILTER_URL_KEYS[
            INSTALLED_TOOLS_FILTERABLE_FIELDS.ENDPOINT
          ],
      },
    } satisfies ColumnConfig<InstalledToolListItem>,
    {
      id: 'firstDetected',
      header: 'First Detected',
      timestamp: true,
      minWidth: TIMESTAMP_COLUMN_MIN_WIDTH_LONG_HEADER,
      accessor: 'firstDetected',
      renderCell: ({ row }) => <FirstDetectedCell row={row} />,
      sortable: true,
      sortField: 'firstDetected',
      sortKey: 'fd',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          INSTALLED_TOOLS_FILTER_URL_KEYS[
            INSTALLED_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: 'lastSeen',
      header: 'Last Seen',
      timestamp: true,
      accessor: 'lastSeen',
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      sortable: true,
      sortField: 'lastSeen',
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          INSTALLED_TOOLS_FILTER_URL_KEYS[
            INSTALLED_TOOLS_FILTERABLE_FIELDS.LAST_SEEN
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}
